import React, { useCallback, useMemo } from 'react'
import {
    useLiveClient,
    useLiveSpaceSummary,
    useLiveUserDeviceState,
} from '@components/Live/LiveClientProvider'
import { makeDefaultChannelStreamId } from '@towns-protocol/sdk'
import { Box, ButtonText, Icon, Stack, Text } from '@ui'
import { addressFromSpaceId } from 'ui/utils/utils'
import { SpaceData } from 'use-towns-client'

import { NavItem } from '../../NavItem/_NavItem'
import { ChannelNavGroup } from '../../NavItem/ChannelNavGroup'

import { SoundWaveIcon } from './SoundWaveIcon'
import * as styles from './SoundWaveIcon.css'
import { SpaceSideBarListItem } from './SpaceSideBarListItem'

type LiveChannelInfo = {
    channelId: string
    channelLabel: string
    stageCount: number
    listenerCount: number
    isUserLive: boolean // Whether the current user is live in this channel
}

/** Format count to compact display (e.g., 999, 1.2k, 10k) */
function formatCount(count: number): string {
    if (count < 1000) {
        return String(count)
    }
    if (count < 10000) {
        return `${(count / 1000).toFixed(1)}k`
    }
    if (count < 1000000) {
        return `${Math.floor(count / 1000)}k`
    }
    return `${(count / 1000000).toFixed(1)}m`
}

export const LiveChannelSection = React.memo(function LiveChannelSection(props: {
    space: SpaceData
}) {
    const { space } = props
    const deviceState = useLiveUserDeviceState()
    const spaceSummary = useLiveSpaceSummary(space.id)

    // Get the default channel ID
    const defaultChannelId = useMemo(() => {
        const spaceAddress = addressFromSpaceId(space.id)
        if (!spaceAddress) {
            return undefined
        }
        return makeDefaultChannelStreamId(spaceAddress)
    }, [space.id])

    // Check which channel the user is currently live in (if any)
    const userLiveChannelId = useMemo(() => {
        if (deviceState?.type === 'live' && deviceState.spaceId === space.id) {
            return deviceState.channelId
        }
        return undefined
    }, [deviceState, space.id])

    // Build list of live channels
    const liveChannels = useMemo((): LiveChannelInfo[] => {
        if (!spaceSummary?.channels || !defaultChannelId) {
            return []
        }

        const channels: LiveChannelInfo[] = []
        const allChannels = space.channelGroups?.flatMap((g) => g.channels) ?? []
        const effectiveUserLiveChannelId = userLiveChannelId

        // Always include default channel (general)
        const defaultChannel = allChannels.find((c) => c.id === defaultChannelId)
        const defaultChannelState = spaceSummary.channels[defaultChannelId]

        if (defaultChannel) {
            channels.push({
                channelId: defaultChannelId,
                channelLabel: defaultChannel.label + '/live',
                stageCount: defaultChannelState?.stage.totalCount ?? 0,
                listenerCount: defaultChannelState?.listeners.totalCount ?? 0,
                isUserLive: effectiveUserLiveChannelId === defaultChannelId,
            })
        }

        // Add other channels that have activity
        for (const [channelId, channelState] of Object.entries(spaceSummary.channels)) {
            if (channelId === defaultChannelId) {
                continue
            }

            const hasActivity =
                channelState.stage.totalCount > 0 || channelState.listeners.totalCount > 0

            if (hasActivity) {
                const channel = allChannels.find((c) => c.id === channelId)
                if (channel) {
                    channels.push({
                        channelId,
                        channelLabel: channel.label + '/live',
                        stageCount: channelState.stage.totalCount,
                        listenerCount: channelState.listeners.totalCount,
                        isUserLive: effectiveUserLiveChannelId === channelId,
                    })
                }
            }
        }

        return channels
    }, [spaceSummary?.channels, space.channelGroups, defaultChannelId, userLiveChannelId])

    // Only show if deviceState exists (user is connected to live)
    if (!deviceState) {
        return null
    }

    // Don't show section if no channels to display
    if (liveChannels.length === 0) {
        return null
    }

    return (
        <>
            <SpaceSideBarListItem key="live-header">
                <ChannelNavGroup label="Live" />
            </SpaceSideBarListItem>
            {liveChannels.map((channel) => (
                <SpaceSideBarListItem key={`live-${channel.channelId}`}>
                    <LiveChannelNavItem
                        spaceId={space.id}
                        channelId={channel.channelId}
                        channelLabel={channel.channelLabel}
                        stageCount={channel.stageCount}
                        listenerCount={channel.listenerCount}
                        isUserLive={channel.isUserLive}
                    />
                </SpaceSideBarListItem>
            ))}
        </>
    )
})

const LiveChannelNavItem = React.memo(function LiveChannelNavItem(props: {
    spaceId: string
    channelId: string
    channelLabel: string
    stageCount: number
    listenerCount: number
    isUserLive: boolean
}) {
    const { spaceId, channelId, channelLabel, stageCount, listenerCount, isUserLive } = props

    const { joinChannel } = useLiveClient()

    const onClick = useCallback(async () => {
        await joinChannel(spaceId, channelId)
    }, [joinChannel, spaceId, channelId])

    const totalUsers = stageCount + listenerCount
    const isChannelLive = totalUsers > 0

    return (
        <NavItem
            id={`live-${channelId}`}
            exact={false}
            paddingY="xxs"
            minHeight="x5"
            onClick={onClick}
        >
            <Stack horizontal width="100%" gap="sm" alignItems="center" rounded="xs">
                <Box centerContent shrink={false}>
                    {isUserLive ? (
                        // State 3: User is live in this channel - solid green with sound waves
                        <SoundWaveIcon size={32} />
                    ) : isChannelLive ? (
                        // State 2: Channel is live but user is not in it - green outline with pulse
                        <Box centerContent square="square_lg" className={styles.liveOutlineClass}>
                            <Icon type="microphone" color="positive" size="square_sm" />
                        </Box>
                    ) : (
                        // State 1: Default/general - dark background
                        <Icon
                            type="microphone"
                            background="level2"
                            color="positive"
                            size="square_lg"
                            padding="x0.75"
                            rounded="sm"
                        />
                    )}
                </Box>
                <ButtonText grow color="gray2">
                    {channelLabel}
                </ButtonText>
                {/* Stats badge */}
                {totalUsers > 0 && (
                    <Stack horizontal gap="sm" alignItems="center" paddingX="sm" shrink={false}>
                        {stageCount > 0 && (
                            <Stack horizontal gap="xxs" alignItems="center">
                                <Icon type="microphone" size="square_xxs" color="positive" />
                                <Text size="xs" fontWeight="medium" color="positive">
                                    {formatCount(stageCount)}
                                </Text>
                            </Stack>
                        )}
                        {listenerCount > 0 && (
                            <Stack horizontal gap="xxs" alignItems="center">
                                <Icon type="people" size="square_xxs" color="positive" />
                                <Text size="xs" fontWeight="medium" color="positive">
                                    {formatCount(listenerCount)}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
        </NavItem>
    )
})
